import { useRef, useEffect, ReactNode, useState } from "react";

interface ScrollSectionProps {
  children: ReactNode;
  className?: string;
  effect?: "rise" | "fade-scale" | "unveil";
  delay?: number;
  mountWhenVisible?: boolean;
  placeholderHeight?: number;
  id?: string;
}

/**
 * Scroll-reveal wrapper. Uses IntersectionObserver to toggle a single
 * `data-revealed` attribute on the wrapper element directly (no React state,
 * no re-renders during scroll). The visual transition is driven entirely by
 * CSS, which avoids interleaved layout reads/writes (forced reflows) caused
 * by per-frame React reconciliation while scrolling.
 */
export default function ScrollSection({
  children,
  className = "",
  effect = "rise",
  mountWhenVisible = false,
  placeholderHeight,
  id,
}: ScrollSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [hasMountedChildren, setHasMountedChildren] = useState(!mountWhenVisible);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Honor prefers-reduced-motion: reveal immediately, no transition.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.setAttribute("data-revealed", "true");
      if (mountWhenVisible) setHasMountedChildren(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (mountWhenVisible) setHasMountedChildren(true);
            el.setAttribute("data-revealed", "true");
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      id={id}
      data-scroll-effect={effect}
      className={`scroll-section ${className}`}
    >
      {hasMountedChildren ? children : placeholderHeight ? <div style={{ minHeight: placeholderHeight }} /> : null}
    </div>
  );
}
